import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { 
  
  
  Share2, 
  Copy, 
  Check,
  Music2
} from 'lucide-react'
import { toast } from 'sonner'
import { trackShareEvent } from '@/lib/shareTracking'
import TikTokShareDialog from './TikTokShareDialog'

interface ShareButtonsProps {
  url: string
  title: string
  description?: string
  hashtags?: string[]
  className?: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  articleId?: string
  affiliateLinks?: string[]
}

export default function SocialShareButtons({
  url,
  title,
  description = '',
  hashtags = [],
  className = '',
  showLabel = true,
  size = 'md',
  articleId,
  affiliateLinks = []
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)
  const [tiktokDialogOpen, setTiktokDialogOpen] = useState(false)

  // Ensure URL is absolute
  const shareUrl = url.startsWith('http') ? url : `https://tips.sydneygotoman.com${url}`
  
  // Encode for URL parameters
  const encodedUrl = encodeURIComponent(shareUrl)
  const encodedTitle = encodeURIComponent(title)
  const encodedDescription = encodeURIComponent(description)
  const hashtagString = hashtags.map(tag => tag.replace('#', '')).join(',')

  // Track share events
  const trackShare = async (platform: string) => {
    await trackShareEvent(platform, shareUrl, title, articleId)
  }

  // Facebook Share
  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`
    window.open(fbUrl, '_blank', 'width=600,height=400')
    trackShare('facebook')
    toast.success('Opening Facebook...')
  }

  // LinkedIn Share
  const shareToLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
    window.open(linkedInUrl, '_blank', 'width=600,height=600')
    trackShare('linkedin')
    toast.success('Opening LinkedIn...')
  }

  // TikTok Share - Open custom dialog
  const shareToTikTok = () => {
    setTiktokDialogOpen(true)
    trackShare('tiktok')
  }

  // Copy Link
  const copyToClipboard = async (text?: string) => {
    const textToCopy = text || shareUrl
    try {
      await navigator.clipboard.writeText(textToCopy)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
      trackShare('copy_link')
    } catch (err) {
      toast.error('Failed to copy link')
    }
  }

  // Native Share API (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl
        })
        trackShare('native_share')
      } catch (err) {
        console.log('Error sharing:', err)
      }
    } else {
      toast.error('Sharing not supported on this browser')
    }
  }

  const buttonSizes = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12'
  }

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-sm font-medium mr-2">
          Share:
        </span>
      )}

      {/* Facebook */}
      <Button
        onClick={shareToFacebook}
        variant="outline"
        size="icon"
        className={`${buttonSizes[size]} bg-[#1877F2] hover:bg-[#0C63D4] text-white border-0 transition-all hover:scale-110`}
        title="Share on Facebook"
      >
        <Share2 className={iconSizes[size]} />
      </Button>

      {/* LinkedIn */}
      <Button
        onClick={shareToLinkedIn}
        variant="outline"
        size="icon"
        className={`${buttonSizes[size]} bg-[#0A66C2] hover:bg-[#004182] text-white border-0 transition-all hover:scale-110`}
        title="Share on LinkedIn"
      >
        <Share2 className={iconSizes[size]} />
      </Button>

      {/* TikTok */}
      <Button
        onClick={shareToTikTok}
        variant="outline"
        size="icon"
        className={`${buttonSizes[size]} bg-black hover:bg-gray-800 text-white border-0 transition-all hover:scale-110`}
        title="Share on TikTok"
      >
        <Music2 className={iconSizes[size]} />
      </Button>

      {/* Copy Link */}
      <Button
        onClick={() => copyToClipboard()}
        variant="outline"
        size="icon"
        className={`${buttonSizes[size]} transition-all hover:scale-110`}
        title="Copy Link"
      >
        {copied ? (
          <Check className={`${iconSizes[size]} text-green-500`} />
        ) : (
          <Copy className={iconSizes[size]} />
        )}
      </Button>

      {/* Native Share (mobile) */}
      {navigator.share && (
        <Button
          onClick={handleNativeShare}
          variant="outline"
          size="icon"
          className={`${buttonSizes[size]} transition-all hover:scale-110`}
          title="Share"
        >
          <Share2 className={iconSizes[size]} />
        </Button>
      )}

      {/* TikTok Dialog */}
      <TikTokShareDialog
        open={tiktokDialogOpen}
        onOpenChange={setTiktokDialogOpen}
        title={title}
        description={description}
        url={shareUrl}
        hashtags={hashtags}
        affiliateLinks={affiliateLinks}
      />
    </div>
  )
}
